import "./TermsGate.css";
import { useEffect, useState } from "react";
import { termsSections, TERMS_LAST_UPDATED } from "./TermsContent";
import { PRIVACY_PATH } from "./PrivacyPolicy";
import Logo from "../assests/images/Logo.png";

const STORAGE_KEY = "anushthan-terms-accepted";
const REVIEW_EVENT = "anushthan:review-terms";

// Reopens the popup for someone who has already accepted, so the Terms stay
// readable from the footer instead of disappearing after the first visit.
export const openTermsPopup = () => {
  window.dispatchEvent(new Event(REVIEW_EVENT));
};

// The accepted version is stored alongside the flag, so revising
// TERMS_LAST_UPDATED asks returning visitors to accept the new Terms.
const readAcceptance = () => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    const acceptance = stored ? JSON.parse(stored) : null;

    return acceptance && acceptance.version === TERMS_LAST_UPDATED
      ? acceptance
      : null;
  } catch (error) {
    // Storage can be unavailable or hold a stale value (private browsing,
    // blocked cookies). Ask again rather than assume acceptance.
    return null;
  }
};

const formatAcceptedOn = (acceptance) => {
  const acceptedOn = new Date(acceptance.acceptedAt);

  return Number.isNaN(acceptedOn.getTime())
    ? null
    : acceptedOn.toLocaleDateString("en-IN", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
};

const TermsGate = () => {
  const [acceptance, setAcceptance] = useState(readAcceptance);
  const [reviewing, setReviewing] = useState(false);
  const [checked, setChecked] = useState(false);

  const accepted = Boolean(acceptance);
  // The Privacy Policy must be readable without accepting anything first (app reviewers link to it).
  const onPrivacyPage =
    window.location.pathname.replace(/\/+$/, "") === PRIVACY_PATH;
  const isOpen = reviewing || (!accepted && !onPrivacyPage);

  useEffect(() => {
    const handleReviewRequest = () => setReviewing(true);

    window.addEventListener(REVIEW_EVENT, handleReviewRequest);

    return () => window.removeEventListener(REVIEW_EVENT, handleReviewRequest);
  }, []);

  // Hold the page still behind the popup while it is open.
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  // Escape closes the popup only when it is being re-read, never when it is
  // still gating access to the site.
  useEffect(() => {
    if (!reviewing) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setReviewing(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [reviewing]);

  if (!isOpen) {
    return null;
  }

  const handleAccept = () => {
    const newAcceptance = {
      version: TERMS_LAST_UPDATED,
      acceptedAt: new Date().toISOString(),
    };

    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(newAcceptance));
    } catch (error) {
      // Acceptance still applies to this visit even if it cannot be stored.
    }

    setAcceptance(newAcceptance);
  };

  const acceptedOn = accepted ? formatAcceptedOn(acceptance) : null;

  return (
    <div
      className="terms-gate-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="terms-gate-title"
    >
      <div className="terms-gate-dialog">
        <div className="terms-gate-header">
          <img className="terms-gate-logo" src={Logo} alt="Anushthan Idea Labs" />
          <h2 id="terms-gate-title">
            Terms <span className="text-yellow">&</span> Conditions
          </h2>
          <p className="terms-gate-subtitle">
            {accepted
              ? `Last updated: ${TERMS_LAST_UPDATED}`
              : `Please read and accept these Terms to continue to our website. Last updated: ${TERMS_LAST_UPDATED}`}
          </p>
        </div>

        <div className="terms-gate-body terms-clause">
          {termsSections.map((section, index) => (
            <section key={section.id} className="terms-gate-clause">
              <h3>
                <span className="terms-gate-number">{index + 1}.</span>
                {section.title}
              </h3>
              {section.body}
            </section>
          ))}
        </div>

        <div className="terms-gate-footer">
          {accepted ? (
            <>
              <p className="terms-gate-note">
                {acceptedOn
                  ? `You accepted these Terms on ${acceptedOn}.`
                  : "You have accepted these Terms."}
              </p>
              <button
                type="button"
                className="terms-gate-accept terms-gate-close"
                onClick={() => setReviewing(false)}
              >
                Close
              </button>
            </>
          ) : (
            <>
              <label
                className="terms-gate-check"
                htmlFor="terms-gate-accept-check"
              >
                <input
                  id="terms-gate-accept-check"
                  type="checkbox"
                  checked={checked}
                  onChange={(event) => setChecked(event.target.checked)}
                />
                <span>
                  I have read, understood and accept the Terms and Conditions.
                </span>
              </label>
              <button
                type="button"
                className="terms-gate-accept"
                disabled={!checked}
                onClick={handleAccept}
              >
                I Accept
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TermsGate;
